import React, { useState, useEffect, useCallback } from 'react';
import {
  Trash2, Search, Music, RefreshCw, AlertTriangle,
  Play, Pause, Copy, CheckSquare, Square, Filter, ShieldAlert,
  Pencil, Star,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * TrackManager — curate the MINDESIGN Radio universe.
 *
 *   • Lists every active song from /api/music/songs (paginated, full library)
 *   • Search by title / artist / genre
 *   • Auto-flags suspected duplicates (same title OR same audio filename slug)
 *   • Soft-delete a single track or any selection (sets active=false in DB)
 *   • Inline preview-play to verify the right track before nuking
 */
export const TrackManager = ({ adminToken }) => {
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showDupesOnly, setShowDupesOnly] = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [editing, setEditing] = useState(null); // song object being edited
  const [savingEdit, setSavingEdit] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [playingId, setPlayingId] = useState(null);
  const [audio, setAudio] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      // Pull everything page-by-page (max 100 per call)
      const all = [];
      let page = 1;
      while (true) {
        const res = await fetch(`${API_URL}/api/music/songs?limit=100&page=${page}`);
        const data = await res.json();
        all.push(...(data.songs || []));
        if (page >= (data.total_pages || 1)) break;
        page += 1;
        if (page > 50) break; // safety
      }
      setSongs(all);
    } catch (err) {
      toast.error('Could not load tracks. ' + (err?.message || ''));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Duplicate detection — same uppercased title or same filename slug
  const dupeKey = (s) => {
    const file = (s.full_audio_url || '').split('/').pop().toLowerCase();
    // Strip suffixes like _1, _2, _new, _alt, _remix
    const slug = file
      .replace(/\.(mp3|wma|wav|m4a|aac|ogg|flac)$/i, '')
      .replace(/[_-]+(?:new|alt|remix|mix|v\d+|\d+)$/i, '')
      .toLowerCase();
    return slug;
  };
  const titleKey = (s) => (s.title || '').trim().toUpperCase().replace(/\s*\(MIX\s*\d+\)\s*$/i, '');

  const dupeIds = (() => {
    const byFile = new Map();
    const byTitle = new Map();
    songs.forEach((s) => {
      const fk = dupeKey(s);
      const tk = titleKey(s);
      if (fk) byFile.set(fk, [...(byFile.get(fk) || []), s.id]);
      if (tk) byTitle.set(tk, [...(byTitle.get(tk) || []), s.id]);
    });
    const flagged = new Set();
    [...byFile.values(), ...byTitle.values()].forEach((ids) => {
      if (ids.length > 1) ids.forEach((id) => flagged.add(id));
    });
    return flagged;
  })();

  const visible = songs
    .filter((s) => {
      if (showDupesOnly && !dupeIds.has(s.id)) return false;
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return (
        (s.title || '').toLowerCase().includes(q) ||
        (s.artist || '').toLowerCase().includes(q) ||
        (s.genre || '').toLowerCase().includes(q)
      );
    });

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAllVisible = () => {
    const allVisible = visible.every((s) => selectedIds.has(s.id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisible) {
        visible.forEach((s) => next.delete(s.id));
      } else {
        visible.forEach((s) => next.add(s.id));
      }
      return next;
    });
  };

  const previewPlay = (s) => {
    if (audio) {
      audio.pause();
      setAudio(null);
    }
    if (playingId === s.id) { setPlayingId(null); return; }
    const url = s.full_audio_url?.startsWith('http')
      ? s.full_audio_url
      : `${API_URL}${s.full_audio_url}`;
    const a = new Audio(url);
    a.volume = 0.6;
    a.play().catch(() => toast.error('Unable to play preview.'));
    a.onended = () => setPlayingId(null);
    setAudio(a);
    setPlayingId(s.id);
  };

  const deleteOne = async (id) => {
    setDeleting(true);
    try {
      const res = await fetch(`${API_URL}/api/music/songs/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const removed = songs.find((s) => s.id === id);
      toast.success(`Removed "${removed?.title || id}" from the universe.`);
      setSongs((prev) => prev.filter((s) => s.id !== id));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    } catch (err) {
      toast.error('Delete failed. ' + (err?.message || ''));
    } finally {
      setDeleting(false);
      setConfirmId(null);
    }
  };

  const deleteSelected = async () => {
    setDeleting(true);
    const ids = Array.from(selectedIds);
    let ok = 0, fail = 0;
    for (const id of ids) {
      try {
        const res = await fetch(`${API_URL}/api/music/songs/${id}`, { method: 'DELETE' });
        if (res.ok) ok += 1; else fail += 1;
      } catch (_) { fail += 1; }
    }
    setSongs((prev) => prev.filter((s) => !selectedIds.has(s.id)));
    setSelectedIds(new Set());
    setDeleting(false);
    setConfirmBulk(false);
    if (fail) toast.warning(`Removed ${ok}, failed ${fail}.`);
    else toast.success(`Removed ${ok} track${ok === 1 ? '' : 's'} from the universe.`);
  };

  const saveEdit = async (patch) => {
    if (!editing) return;
    setSavingEdit(true);
    try {
      const res = await fetch(`${API_URL}/api/music/songs/${editing.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const updated = json.song;
      setSongs((prev) => prev.map((s) => (s.id === editing.id ? { ...s, ...updated } : s)));
      toast.success(`Updated "${updated.title}"`);
      setEditing(null);
    } catch (err) {
      toast.error('Update failed. ' + (err?.message || ''));
    } finally {
      setSavingEdit(false);
    }
  };

  const dupesCount = Array.from(dupeIds).length;

  return (
    <div className="space-y-6" data-testid="track-manager">
      {/* Header */}
      <div className="bg-gunmetal-900 rounded-2xl p-6 border border-steel-700/30">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center">
              <Music className="mr-2 text-accent-red" size={24} />
              Track Manager
            </h2>
            <p className="text-steel-400 text-sm mt-1">
              {songs.length} active track{songs.length === 1 ? '' : 's'} ·{' '}
              {dupesCount > 0 ? (
                <span className="text-amber-400">{dupesCount} possible duplicate{dupesCount === 1 ? '' : 's'} flagged</span>
              ) : (
                <span className="text-green-400">no duplicates detected</span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              onClick={fetchAll}
              variant="outline"
              className="bg-gunmetal-800 border-steel-700 text-steel-200 hover:bg-gunmetal-700"
              data-testid="track-manager-refresh"
            >
              <RefreshCw size={16} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              type="button"
              onClick={() => setConfirmBulk(true)}
              disabled={selectedIds.size === 0 || deleting}
              className="bg-red-600 hover:bg-red-500 text-white disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="track-manager-delete-selected"
            >
              <Trash2 size={16} className="mr-2" />
              Remove Selected ({selectedIds.size})
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[260px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-steel-500" />
            <Input
              type="text"
              placeholder="Search title, artist or genre…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-gunmetal-800 border-steel-700 text-steel-100 placeholder:text-steel-500"
              data-testid="track-manager-search"
            />
          </div>
          <Button
            type="button"
            onClick={() => setShowDupesOnly((v) => !v)}
            className={`${
              showDupesOnly
                ? 'bg-amber-500 text-gunmetal-950'
                : 'bg-gunmetal-800 text-amber-300 hover:bg-gunmetal-700'
            } font-bold`}
            data-testid="track-manager-toggle-dupes"
          >
            <Copy size={16} className="mr-2" />
            {showDupesOnly ? 'Showing duplicates only' : 'Show duplicates only'}
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-gunmetal-900 rounded-2xl border border-steel-700/30 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gunmetal-800 text-steel-300 uppercase text-xs tracking-wider">
              <tr>
                <th className="px-4 py-3 text-left">
                  <button
                    type="button"
                    onClick={toggleSelectAllVisible}
                    className="text-steel-300 hover:text-white"
                    data-testid="track-manager-select-all"
                  >
                    {visible.length > 0 && visible.every((s) => selectedIds.has(s.id))
                      ? <CheckSquare size={18} />
                      : <Square size={18} />}
                  </button>
                </th>
                <th className="px-4 py-3 text-left">Title</th>
                <th className="px-4 py-3 text-left">Artist</th>
                <th className="px-4 py-3 text-left">Genre</th>
                <th className="px-4 py-3 text-left">Length</th>
                <th className="px-4 py-3 text-left">Plays</th>
                <th className="px-4 py-3 text-left">Filename</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-steel-500">
                    {loading ? 'Loading the universe…' : 'No tracks match your filter.'}
                  </td>
                </tr>
              )}
              {visible.map((s) => {
                const file = (s.full_audio_url || '').split('/').pop();
                const isDupe = dupeIds.has(s.id);
                const isSelected = selectedIds.has(s.id);
                return (
                  <tr
                    key={s.id}
                    className={`border-t border-steel-700/30 transition-colors ${
                      isSelected
                        ? 'bg-red-500/10'
                        : isDupe
                        ? 'bg-amber-500/5 hover:bg-amber-500/10'
                        : 'hover:bg-gunmetal-800/50'
                    }`}
                    data-testid={`track-row-${s.id}`}
                  >
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => toggleSelect(s.id)}
                        className={isSelected ? 'text-red-400' : 'text-steel-500 hover:text-white'}
                      >
                        {isSelected ? <CheckSquare size={18} /> : <Square size={18} />}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-white font-semibold">
                      {s.featured && (
                        <Star size={12} className="inline-block text-amber-400 fill-amber-400 mr-1.5 -translate-y-px" />
                      )}
                      {s.title}
                      {isDupe && (
                        <span className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-amber-400 bg-amber-400/10 border border-amber-400/30 rounded-full px-2 py-0.5">
                          <Copy size={10} />
                          Possible dupe
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-steel-300">{s.artist || '—'}</td>
                    <td className="px-4 py-3 text-steel-400">{s.genre || '—'}</td>
                    <td className="px-4 py-3 text-steel-400">{s.duration || '—'}</td>
                    <td className="px-4 py-3 text-steel-400">{s.plays ?? 0}</td>
                    <td className="px-4 py-3 text-steel-500 font-mono text-xs truncate max-w-[180px]">{file || '—'}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => previewPlay(s)}
                          className="text-steel-300 hover:text-white hover:bg-gunmetal-700"
                        >
                          {playingId === s.id ? <Pause size={14} /> : <Play size={14} />}
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => setEditing(s)}
                          className="text-steel-300 hover:text-amber-300 hover:bg-amber-500/10"
                          data-testid={`track-edit-${s.id}`}
                          aria-label="Edit track"
                        >
                          <Pencil size={14} />
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => setConfirmId(s.id)}
                          className="bg-red-600/80 hover:bg-red-500 text-white"
                          data-testid={`track-delete-${s.id}`}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Single delete confirm */}
      {confirmId && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-gunmetal-900 border border-red-500/40 rounded-2xl p-6 max-w-md w-full" data-testid="track-confirm-dialog">
            <div className="flex items-start gap-3 mb-4">
              <ShieldAlert className="text-red-400 flex-shrink-0" size={24} />
              <div>
                <h3 className="text-white font-bold text-lg">Remove from the universe?</h3>
                <p className="text-steel-400 text-sm mt-1">
                  "{songs.find((s) => s.id === confirmId)?.title}" will be hidden from the radio,
                  music licensing grid, and Spotify-style queue. The audio file stays on disk so
                  this is reversible from MongoDB if needed.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                onClick={() => setConfirmId(null)}
                variant="ghost"
                className="text-steel-300 hover:bg-gunmetal-800"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => deleteOne(confirmId)}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-500 text-white"
                data-testid="track-confirm-delete"
              >
                <Trash2 size={14} className="mr-2" />
                Yes, remove it
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit dialog */}
      {editing && (
        <EditTrackDialog
          song={editing}
          onCancel={() => setEditing(null)}
          onSave={saveEdit}
          saving={savingEdit}
        />
      )}

      {/* Bulk delete confirm */}
      {confirmBulk && (        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-gunmetal-900 border border-red-500/40 rounded-2xl p-6 max-w-md w-full">
            <div className="flex items-start gap-3 mb-4">
              <AlertTriangle className="text-red-400 flex-shrink-0" size={24} />
              <div>
                <h3 className="text-white font-bold text-lg">
                  Remove {selectedIds.size} track{selectedIds.size === 1 ? '' : 's'}?
                </h3>
                <p className="text-steel-400 text-sm mt-1">
                  Each one will be hidden from the radio + licensing grid. Files stay on disk.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                onClick={() => setConfirmBulk(false)}
                variant="ghost"
                className="text-steel-300 hover:bg-gunmetal-800"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={deleteSelected}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-500 text-white"
                data-testid="track-confirm-bulk-delete"
              >
                <Trash2 size={14} className="mr-2" />
                Yes, remove all {selectedIds.size}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrackManager;

const GENRE_PRESETS = [
  'Rock', 'Synth Rock', 'Funk Rock', 'Reggae Rock', 'Blues Rock',
  'Acoustic', 'Inspirational Rock', 'Dark Rock', 'Alt Rock',
  'Gospel Rock', 'Spiritual', 'Electronic', 'Cinematic',
  'Hip Hop', 'R&B', 'Pop', 'Country', 'Soul',
];

const EditTrackDialog = ({ song, onCancel, onSave, saving }) => {
  const [title, setTitle] = useState(song.title || '');
  const [artist, setArtist] = useState(song.artist || '');
  const [genre, setGenre] = useState(song.genre || 'Rock');
  const [featured, setFeatured] = useState(!!song.featured);
  const [description, setDescription] = useState(song.description || '');
  const [moodText, setMoodText] = useState((song.mood || []).join(', '));
  const [tagsText, setTagsText] = useState((song.tags || []).join(', '));

  const submit = (e) => {
    e.preventDefault();
    const patch = {
      title: title.trim(),
      artist: artist.trim(),
      genre: genre.trim(),
      featured,
      description: description.trim(),
      mood: moodText.split(',').map((s) => s.trim()).filter(Boolean),
      tags: tagsText.split(',').map((s) => s.trim()).filter(Boolean),
    };
    onSave(patch);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="track-edit-dialog"
    >
      <form
        onSubmit={submit}
        className="bg-gunmetal-900 border border-amber-400/40 rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center gap-2 mb-4">
          <Pencil size={18} className="text-amber-400" />
          <h3 className="text-white font-bold text-lg">Edit Track</h3>
          <span className="ml-auto text-[10px] tracking-widest uppercase text-steel-500 font-mono">
            {song.id}
          </span>
        </div>

        <div className="space-y-3">
          <label className="block">
            <span className="text-[10px] tracking-widest uppercase text-steel-400 font-bold">Title</span>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="mt-1 bg-gunmetal-800 border-steel-700 text-steel-100"
              data-testid="track-edit-title"
            />
          </label>

          <label className="block">
            <span className="text-[10px] tracking-widest uppercase text-steel-400 font-bold">Artist</span>
            <Input
              value={artist}
              onChange={(e) => setArtist(e.target.value)}
              className="mt-1 bg-gunmetal-800 border-steel-700 text-steel-100"
              data-testid="track-edit-artist"
            />
          </label>

          <label className="block">
            <span className="text-[10px] tracking-widest uppercase text-steel-400 font-bold">Genre</span>
            <select
              value={genre}
              onChange={(e) => setGenre(e.target.value)}
              className="mt-1 w-full bg-gunmetal-800 border border-steel-700 text-steel-100 rounded-md px-3 py-2 text-sm"
              data-testid="track-edit-genre"
            >
              {GENRE_PRESETS.includes(genre) ? null : <option value={genre}>{genre} (current)</option>}
              {GENRE_PRESETS.map((g) => (
                <option key={g} value={g}>{g}</option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="text-[10px] tracking-widest uppercase text-steel-400 font-bold">Mood (comma-separated)</span>
            <Input
              value={moodText}
              onChange={(e) => setMoodText(e.target.value)}
              className="mt-1 bg-gunmetal-800 border-steel-700 text-steel-100"
              placeholder="e.g. Anthemic, Cinematic, Dark"
              data-testid="track-edit-mood"
            />
          </label>

          <label className="block">
            <span className="text-[10px] tracking-widest uppercase text-steel-400 font-bold">Tags (comma-separated)</span>
            <Input
              value={tagsText}
              onChange={(e) => setTagsText(e.target.value)}
              className="mt-1 bg-gunmetal-800 border-steel-700 text-steel-100"
              placeholder="e.g. rock, anthem, after-dark"
              data-testid="track-edit-tags"
            />
          </label>

          <label className="block">
            <span className="text-[10px] tracking-widest uppercase text-steel-400 font-bold">Description</span>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="mt-1 w-full bg-gunmetal-800 border border-steel-700 text-steel-100 rounded-md px-3 py-2 text-sm"
              data-testid="track-edit-description"
            />
          </label>

          <button
            type="button"
            onClick={() => setFeatured((v) => !v)}
            className={`w-full flex items-center justify-center gap-2 py-2 rounded-md font-bold tracking-widest uppercase text-xs transition-all ${
              featured
                ? 'bg-amber-400/20 text-amber-300 border border-amber-400/50'
                : 'bg-gunmetal-800 text-steel-400 border border-steel-700 hover:bg-gunmetal-700'
            }`}
            data-testid="track-edit-featured"
          >
            <Star size={14} className={featured ? 'fill-amber-300' : ''} />
            {featured ? 'Featured Track' : 'Mark as Featured'}
          </button>
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <Button
            type="button"
            onClick={onCancel}
            variant="ghost"
            className="text-steel-300 hover:bg-gunmetal-800"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={saving}
            className="bg-amber-500 hover:bg-amber-400 text-gunmetal-950 font-bold"
            data-testid="track-edit-save"
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </Button>
        </div>
      </form>
    </div>
  );
};
